import {history, historyKeymap} from '@codemirror/commands'
import {type Command, EditorView, keymap, placeholder, type ViewUpdate} from '@codemirror/view'
import {readOnly} from '@github-ui/code-mirror/extensions/readonly'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import type {FileType} from '@github-ui/use-unified-file-select'
import {PaperclipIcon} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'
import {clsx} from 'clsx'
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {caretFlashExtension} from './codemirror/extensions/caret-flash'
import {dropPositionExtension} from './codemirror/extensions/drop-position'
import {markdownContinuationExtension} from './codemirror/extensions/markdown-continuation'
import {markdownKeymapExtension} from './codemirror/extensions/markdown-keymap'
import {isModPressedField, modPressedExtension} from './codemirror/extensions/mod-pressed'
import {pasteLinksExtension} from './codemirror/extensions/paste-links'
import {proseExtension} from './codemirror/extensions/prose'
import {
  getCurrentSelectionContext,
  initialSelectionContext,
  selectionContextChanged,
  selectionContextExtension,
} from './codemirror/extensions/selection-context'
import {singleLineEditorExtension} from './codemirror/extensions/single-line-editor'
import {styleContentsExtension} from './codemirror/extensions/style-contents'
import {uploadPlaceholderExtension} from './codemirror/extensions/upload-placeholder'
import {visitLinkExtension} from './codemirror/extensions/visit-link'
import {useCompartment} from './codemirror/hooks/use-compartment'
import {SelectionContextProvider} from './context/SelectionContext'
import {ErrorFallback} from './ErrorFallback'
import {type FileUploadResult, useFileHandler} from './file-handler/useFileHandler'
import styles from './HybridMarkdownEditor.module.css'
import {Header} from './toolbar/Header'
import {Toolbar} from './toolbar/Toolbar'
import type {MarkdownViewMode} from './toolbar/ViewSwitch'

// NOTE: These props should be a subset of `MarkdownEditorProps`, so that we can pass props through from the containing component
export interface HybridMarkdownEditorProps {
  'aria-describedby'?: string
  disabled?: boolean
  onChange?: (value: string) => void
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  placeholder?: string
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  acceptedFileTypes?: FileType[]
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
  value: string
  /**
   * Called when the user presses `Ctrl`/`Cmd` + `Enter`. Should always be wired to the same event as clicking the
   * primary action button in the footer.
   */
  onPrimaryAction?: () => void
}

export interface HybridMarkdownEditorRef {
  view: EditorView | null
  focus: () => void
  reset: () => void
  scrollIntoView: (opts?: ScrollIntoViewOptions) => void
  moveCursorToEnd: () => void
}

const DEFAULT_ACCEPTED_FILE_TYPES: FileType[] = ['image/*', 'video/*']

const InnerHybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  function InnerHybridMarkdownEditor(
    {
      value,
      onChange,
      disabled,
      onChangeViewMode,
      placeholder: placeholderText,
      'aria-describedby': ariaDescribedBy,
      acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
      onUploadFile,
      onFilesRejected,
      onPrimaryAction,
    },
    ref,
  ) {
    const [selectionContext, setSelectionContext] = useState(initialSelectionContext)

    /** Tracks the expected value for the next render according to user input. */
    const [expectedValue, setExpectedValue] = useState(value)

    /** Stable tracking reference to `onChange` so we don't have to update editor extensions when the prop changes. */
    const onChangeRef = useTrackingRef(onChange)
    const onUpdate = useCallback(
      (update: ViewUpdate) => {
        if (update.docChanged) {
          const newValue = update.state.doc.toString()
          // As long as the consumer updates `value` state synchronously in its onChange handler, the next render will
          // have both `expectedValue` and `value` updated to the new value and will thus skip the reset step. If the
          // consumer ignores the update or changes `value` to something other than what was expected, we will reset
          // the editor, which will clear selection state. This 'synchronous update' requirement aligns with how React
          // handles regular DOM inputs (see https://react.dev/reference/react-dom/components/input#my-input-caret-jumps-to-the-beginning-on-every-keystroke).
          setExpectedValue(newValue)
          onChangeRef.current?.(newValue)
        }

        // Check for selection context changes
        if (update.transactions.some(tr => selectionContextChanged(tr))) {
          const newContext = getCurrentSelectionContext(update.state)
          setSelectionContext(newContext)
        }
      },
      [onChangeRef],
    )

    const [view, setView] = useState<EditorView | null>(null)

    // Each extension that depends on prop values must be contained in a compartment to avoid reinitializing the editor
    // when the props change. Compartments are constant references so safe to use as dependencies.
    const placeholderCompartment = useCompartment(
      view,
      useMemo(() => placeholder(placeholderText ?? ''), [placeholderText]),
    )

    const readonlyCompartment = useCompartment(
      view,
      useMemo(() => (disabled ? readOnly() : []), [disabled]),
    )

    const a11yAttributesCompartment = useCompartment(
      view,
      useMemo(
        () =>
          EditorView.contentAttributes.of({
            'aria-label': 'Markdown content',
            ...(ariaDescribedBy ? {'aria-describedby': ariaDescribedBy} : {}),
          }),
        [ariaDescribedBy],
      ),
    )

    const onPrimaryActionRef = useTrackingRef(onPrimaryAction)
    const primaryAction: Command = useCallback(() => {
      if (onPrimaryActionRef.current) {
        onPrimaryActionRef.current()
        return true
      }
      return false
    }, [onPrimaryActionRef])

    const extensions = useMemo(
      () => [
        proseExtension,
        singleLineEditorExtension,
        styleContentsExtension,
        uploadPlaceholderExtension,
        dropPositionExtension,
        caretFlashExtension,
        selectionContextExtension,
        pasteLinksExtension,
        markdownContinuationExtension,
        visitLinkExtension(),
        history(),
        // ESLint false positive: we are accessing the ref value in a callback in response to an update, so it's safe
        // eslint-disable-next-line react-hooks/refs
        EditorView.updateListener.of(onUpdate),
        a11yAttributesCompartment,
        modPressedExtension,
        EditorView.contentAttributes.from(isModPressedField, isModPressed => ({
          // Note: Compartments appear to get applied after first render, which causes a flash of unstyled content when
          // `class` is applied inside a compartment. When we add support for a `className` prop, we'll want to keep
          // this line _in addition_ to the new compartment.
          class: clsx(styles.content, 'markdown-body', isModPressed && styles.modPressed),
        })),
        placeholderCompartment,
        readonlyCompartment,
        // ESLint false positive: we are accessing the ref value in a callback in response to an interaction, so it's safe
        // eslint-disable-next-line react-hooks/refs
        markdownKeymapExtension({
          primaryAction,
        }),
        keymap.of(historyKeymap),
      ],
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [onUpdate, primaryAction, placeholderCompartment, readonlyCompartment, a11yAttributesCompartment],
    )

    const containerRef = useRef<HTMLDivElement>(null)

    const fileHandler = useFileHandler({
      value,
      view,
      disabled,
      onUploadFile,
      acceptedFileTypes,
      onFilesRejected,
    })

    // Wrap drop target props to fix the dragleave issue when dragging over child elements
    const wrappedDropTargetProps = useMemo(() => {
      const dropProps = fileHandler?.dropTargetProps
      if (!dropProps) return undefined

      return {
        ...dropProps,
        onDragLeave: (event: React.DragEvent<HTMLDivElement>) => {
          // Only call the original handler if the drag is truly leaving the container
          // Check if the relatedTarget (where the drag is moving to) is outside the container
          if (!(event.currentTarget as Node).contains(event.relatedTarget as Node)) {
            dropProps.onDragLeave?.(event as unknown as React.DragEvent<HTMLDivElement>)
          }
        },
        onDragOver: dropProps.onDragOver,
        onDragEnter: dropProps.onDragEnter,
        onDrop: dropProps.onDrop,
      }
    }, [fileHandler?.dropTargetProps])

    const initialValueRef = useRef(value)
    useEffect(
      function initializeEditor() {
        const editorView = new EditorView({
          parent: containerRef.current!,
          doc: initialValueRef.current,
          extensions,
        })

        setView(editorView)

        return () => {
          editorView.destroy()
          setView(null)
        }
      },
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [extensions],
    )

    useLayoutEffect(
      function syncControlledValue() {
        // If the value prop doesn't match the expected value, overwrite the input with the prop value
        // Only happens if `onChange` is defined, indicating that this is a controlled input
        if (expectedValue !== value && onChange !== undefined) {
          view?.update([view.state.update({changes: {from: 0, to: view.state.doc.length, insert: value}})])
        }
      },
      [expectedValue, value, onChange, view],
    )

    useImperativeHandle(
      ref,
      () => ({
        view,
        focus: () => view?.focus(),
        reset: () => view?.dispatch({changes: {from: 0, to: view.state.doc.length, insert: ''}}),
        scrollIntoView: opts => view?.dom?.scrollIntoView(opts),
        moveCursorToEnd: () => view?.dispatch({selection: {anchor: view.state.doc.length}}),
      }),
      [view],
    )

    return (
      <>
        <Header onChangeViewMode={onChangeViewMode}>
          <SelectionContextProvider value={selectionContext}>
            <Toolbar
              view={view}
              fileUploadButton={
                onUploadFile ? (
                  <ActionBar.IconButton
                    variant="invisible"
                    icon={PaperclipIcon}
                    aria-label="Upload file"
                    {...fileHandler?.clickTargetProps}
                  />
                ) : null
              }
            />
          </SelectionContextProvider>
        </Header>

        <div
          ref={containerRef}
          className={fileHandler?.isDraggedOver ? styles.isDraggedOver : undefined}
          {...(wrappedDropTargetProps ?? {})}
          {...(fileHandler?.pasteTargetProps ?? {})}
        />
      </>
    )
  },
)

export const HybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  // IMPORTANT: Keep this component as minimal as possible, with no logic that might possibly throw an error, since any
  // errors thrown in this component wouldn't be caught by the error boundary
  function HybridMarkdownEditor({onChangeViewMode, ...restProps}, ref) {
    return (
      <div className={styles.container}>
        <ErrorBoundary fallback={<ErrorFallback onChangeViewMode={onChangeViewMode} />}>
          <InnerHybridMarkdownEditor ref={ref} onChangeViewMode={onChangeViewMode} {...restProps} />
        </ErrorBoundary>
      </div>
    )
  },
)

try{ InnerHybridMarkdownEditor.displayName ||= 'InnerHybridMarkdownEditor' } catch {}
try{ InnerHybridMarkdownEditor.displayName ||= 'InnerHybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}